import { applyPlayerOptions } from './';
import { TimedEventType } from '../../../api/presentation';

import { v4 as uuid } from 'uuid';

/**
 * 
 * @param {any} player
 * @param {PresentationData} presentationData
 * @returns {Promise<PresentationData>}
 */
export async function applyPlayerSource(player, presentationData) {
    const videoUrls = presentationData.streams?.[0]?.items ?? [];
    const localize = player.localize?.bind(player);

    if (videoUrls.length > 0) {
        player.src(videoUrls);
        await new Promise(resolve => {
            player.any(['loadeddata', 'loadedmetadata', 'timeupdate'], resolve);
        });
    }
    
    // Apply base presentation data
    player.metadata().datetime = presentationData.metadata.unixTime;
    player.metadata().description = presentationData.metadata.description;
    player.metadata().presenters = presentationData.metadata.presenters;
    player.metadata().supportingLinks = presentationData.metadata.supportingLinks;
    player.metadata().compliancePolicyMarkdown = presentationData.metadata.compliancePolicyMarkdown;
    player.reporting().playbackTicket = presentationData.playerOptions.playbackTicket;

    //Set src for audio descriptions whether or not it's a real url, because the set function determines visibility
    player.audioDescriptions().src = presentationData.audioDescriptionUrl;

    if (presentationData.captions) {
        player.captions().enabled = true;
        configureCaptions(player, presentationData.captions);
    }
    if (presentationData.chapters) {
        player.chapters().enabled = true;
        configureChapters(player, presentationData.chapters, localize);
    }
    if (presentationData.hotspots) {
        player.hotspots().setIs360(presentationData.metadata.is360Video);
        player.hotspots().add(presentationData.hotspots);
    }

    if (presentationData.timedEvents) {
        for (const timedEvent of presentationData.timedEvents) {
            // we're handling chapters in their own section, so let's not repeat it here
            if (timedEvent.Type === TimedEventType.Chapter || timedEvent.Type === TimedEventType.Hotspot) {
                continue;
            }
            player.timedEvents().add({
                id: timedEvent.Id,
                time: timedEvent.Time / 1000,
                type: timedEvent.Type,
                payload: timedEvent.Text
            });
        }
    }
    // Apply player options
    const { playerOptions } = presentationData;
    applyPlayerOptions(player, playerOptions);

    if (presentationData.streams.length === 1 && presentationData.streams[0].audioOnly === true) {
        player.audioOnly().hideAudioOnlyVideo();
        player.settings().enablePIP = false;
    }

    if (player.downloads().enabled) {
        for (const { text, url } of presentationData.downloads) {
            player.downloads().add(url, localize(text));
        }
    }

    return presentationData;
}

function configureCaptions(player, captionInfos) {
    captionInfos.forEach(captionInfo => {
        const { LanguageCode, Language, CaptionsUrl, DetailedLanguageCode } = captionInfo;
        player.captions().set(LanguageCode, Language, CaptionsUrl, DetailedLanguageCode);
    });
    // Check and try to load selected language from previous play session
    const initialTrackLanguage = player.captions().initialTrackLanguage;
    const initialTextTrack = player.captions().getCaptionTrack(initialTrackLanguage);
    if (initialTextTrack) {
        initialTextTrack.mode = 'showing';
    }
}

function configureChapters(player, chapterInfos, localize) {
    //Adds a seekbar segment with a blank label that starts at 0:00 and ends at the beginning of the first configured chapter
    //Fills the blank seekbar space at the beginning created when breaking up the seekbar into chapter sections
    if (chapterInfos[0]?.Time !== 0.0 && chapterInfos[0] != null) {
        const currentChapterStart = 0.0 / 1000;
        const nextChapterStart = (chapterInfos[0]?.Time ?? 0) / 1000;
        const currentChapterEnd = nextChapterStart || player.duration();

        player.seekBarSegments().add({
            startTime: currentChapterStart,
            endTime: currentChapterEnd,
            progress: true
        });
    }
    for (let i = 0; i < chapterInfos.length; i++) {
        const chapter = chapterInfos[i];
        const currentChapterStart = chapter.Time / 1000;
        let nextChapterStart;
        if (chapterInfos[i + 1]?.Time != null) {
            nextChapterStart = (chapterInfos[i + 1].Time) / 1000;
        } else {
            nextChapterStart = null;
        }
        let currentChapterEnd;
        if (nextChapterStart != null) {
            currentChapterEnd = nextChapterStart;
        } else {
            currentChapterEnd = player.duration();
        }
        const cue = new VTTCue(currentChapterStart, currentChapterEnd, chapter.Text);
        cue.id = chapter.Id;

        if (!cue.text) {
            cue.text = localize('Chapter');
        }

        player.chapters().add(cue);
        //creates a timed event for the chapter that lasts as long as the chapter itself
        player.timedEvents().add({
            id: cue.id,
            time: currentChapterStart,
            duration: currentChapterEnd - currentChapterStart,
            type: TimedEventType.Chapter,
            payload: cue.text
        });
    }
}